// CardCatalog — Inhalt des Karten-Katalogs: Reiterkopf, Ablage-Bedienung, Kartenraster.
// Das Bauteil holt seine Daten selbst (Suche, Deck-Kategorien) und kennt seinen Rahmen
// nicht — dieselbe Datei füllt die Seitenspalte des Editors und ein eigenes Fenster.
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Sparkles, Wand2, X } from 'lucide-react';
import Select from '../../../ui/Select';
import Tabs from '../../../ui/Tabs';
import { useToast } from '../../../ui/Toast';
import { formatCategoryName } from '../../../ui/formatCategoryName';
import { json } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';
import CardDetail from '../../../components/CardDetail';
import { useCardBrowserFilters } from '../hooks/useCardBrowserFilters';
import { deckCategoriesKey, deckUiId } from '../utils/deckIds';
import { notifyAddResult } from '../utils/notifyAddResult';
import CatalogAdminTab from './CatalogAdminTab';
import CatalogSearchTab from './CatalogSearchTab';
import CatalogSuggestionsTab from './CatalogSuggestionsTab';
import { useDropSuggestionFromCache } from './useDeckSuggestions';
import './CardCatalog.css';

/** Wert des Ziel-Wählers, solange keine Kategorie gewählt ist — der Server ermittelt sie. */
const AUTO_TARGET = 'auto';

/**
 * Karten-Katalog eines virtuellen Decks.
 *
 *   <CardCatalog
 *     deckId={42}
 *     activeTab={tab} onTabChange={setTab}
 *     onAddCard={(scryfallId, { categoryId }) => Promise}
 *     isAdmin={isAdmin}
 *     dndEnabled
 *   />
 *
 * Props:
 *   deckId      — numerische ID des virtuellen Decks (die Vorschlags-Endpunkte nehmen die
 *                 Zahl; Kategorie-Abfrage und ✦-Reiter bilden daraus per `deckUiId` die
 *                 UI-Deck-ID `virtual_<id>`)
 *   activeTab   — 'suggestions' | 'search' | 'admin'; der Rahmen führt den Zustand
 *   onTabChange — (tab) => void
 *   onAddCard   — `(scryfallId, { categoryId }) => Promise<{card, duplicate, undo?}|null>`.
 *                 Der Rahmen bildet den Aufruf auf `addCardByScryfallId(scryfallId,
 *                 { categoryId })` ab. `categoryId` ist `undefined`, solange der Ziel-Wähler
 *                 auf „Automatisch" steht — dann ermittelt der Server die Kategorie. Rückgabe
 *                 `{duplicate:true}` → Hinweis-Toast, `null` → der Rahmen hat den Fehler
 *                 bereits gemeldet, eine Ausnahme meldet der Katalog selbst. `undo` ist die
 *                 Gegenaktion des Rahmens; wer einen Rückgängig-Stapel führt (Editor-Fenster),
 *                 gibt sie mit und der Erfolgs-Toast bekommt einen „Rückgängig"-Knopf — das
 *                 Zweitfenster hat keinen Stapel und lässt sie weg.
 *                 Fehlt die Prop, zeigt der Katalog keine Plus-Knöpfe (fremdes Deck).
 *   isAdmin     — blendet den ✦-Reiter ein
 *   dndEnabled  — gibt die Kacheln als Drag-Quellen frei; den Zieh-Kontext stellt der Rahmen
 *   initialColorIdentity — optionale Farbliste (`['C','R','G']`), mit der der Suche-Reiter
 *                 startet; der Rahmen bildet sie aus den Commander-Karten des Decks
 *                 (`utils/commanderColorFilter.js`). Reiner STARTWERT — der Nutzer darf
 *                 die Farben abwählen. Gilt für beide Reiter; im Vorschläge-Reiter ändert
 *                 der Startwert die Liste nicht (die Rangliste ist ohnehin auf die
 *                 Farbidentität begrenzt), er macht die Grenze sichtbar.
 *   openInWindowHref — gesetzt, zeigt der Reiterkopf ein ⧉ neben den Reitern, das die
 *                 URL in einem neuen Fenster öffnet (Katalog auf einem zweiten Monitor).
 *                 Der Rahmen liefert die URL (`/decks/<UI-Deck-ID>/katalog`) — der Katalog
 *                 kennt nur die numerische ID. Ohne die Prop bleibt der Knopf weg, so
 *                 auf der Zweitfenster-Seite selbst (die zeigt sich nicht noch einmal).
 *   onClose     — gesetzt, zeigt der Reiterkopf rechts (nach ⧉) ein ✕ mit
 *                 `aria-label="Katalog schließen"`. Ohne die Prop bleibt der Knopf weg, so
 *                 auf der Zweitfenster-Seite selbst (die schließt der Browser).
 *
 * Pflicht des Rahmens: Die Kategorien des Ziel-Wählers liegen unter dem Query-Key
 * `deckCategoriesKey(deckId)` (`utils/deckIds.js`). Wer Kategorien anlegt, umbenennt oder
 * löscht, ruft danach `queryClient.invalidateQueries({ queryKey: deckCategoriesKey(deckId) })` — die
 * Kategorie-Verwaltung des Editors schreibt an React Query vorbei, ohne diesen Aufruf zeigt
 * der Wähler einen veralteten Stand.
 */
export default function CardCatalog({
  deckId,
  activeTab = 'search',
  onTabChange,
  onAddCard,
  isAdmin = false,
  dndEnabled = false,
  openInWindowHref,
  onClose,
  initialColorIdentity,
}) {
  const { push: pushToast } = useToast();
  const canAdd = typeof onAddCard === 'function';
  const vorschlagEntnehmen = useDropSuggestionFromCache(deckId);
  // Der Reiter kommt aus einer gespeicherten Präferenz. Wer den ✦-Reiter zuletzt offen
  // hatte und heute kein Admin mehr ist, bekäme sonst einen Reiterkopf ohne ausgewählten
  // Reiter und darunter nichts.
  const sichtbarerTab = activeTab === 'admin' && !isAdmin ? 'suggestions' : activeTab;
  const zielId = useId();

  const [targetCategoryId, setTargetCategoryId] = useState(AUTO_TARGET);
  const [addingId, setAddingId] = useState(null);
  const [detail, setDetail] = useState(null);

  // Der Filter-Zustand der Reiter liegt hier, nicht im Reiter selbst: `Tabs.Panel` rendert
  // den inaktiven Reiter nicht, sein State wäre nach jedem Reiterwechsel weg.
  //
  // Beide Reiter starten in den Farben des Commanders — die Suche, weil sie sonst alles zeigt,
  // die Vorschläge, damit man sieht, WARUM nur diese Farben kommen (serverseitig grenzt die
  // Rangliste ohnehin auf die Farbidentität ein; der Filter ändert die Liste nicht).
  const [searchFilters, patchSearchFilters] = useCardBrowserFilters(initialColorIdentity, deckId);
  const [suggestionFilters, patchSuggestionFilters] = useCardBrowserFilters(initialColorIdentity, deckId);

  const categoriesQuery = useQuery({
    queryKey: deckCategoriesKey(deckId),
    queryFn: ({ signal }) => json(`/api/decks/virtual_${deckId}/categories`, { signal }),
    enabled: deckId != null,
  });

  const categories = useMemo(
    () => [...(categoriesQuery.data?.categories || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [categoriesQuery.data]
  );

  // Eine gelöschte Kategorie darf nicht als Ziel kleben bleiben — jeder Add liefe sonst
  // in einen Fehlschlag, dessen Grund nicht auf dem Bildschirm steht.
  useEffect(() => {
    if (targetCategoryId === AUTO_TARGET || !categoriesQuery.isSuccess) return;
    if (!categories.some((c) => String(c.id) === targetCategoryId)) setTargetCategoryId(AUTO_TARGET);
  }, [categories, categoriesQuery.isSuccess, targetCategoryId]);

  const targetOptions = useMemo(() => [
    { value: AUTO_TARGET, label: 'Automatisch', icon: Wand2 },
    ...(categories.length > 0 ? [{ divider: true, key: 'kategorien' }] : []),
    ...categories.map((c) => ({ value: String(c.id), label: <span>{formatCategoryName(c.name)}</span> })),
  ], [categories]);
  const zielWert = targetCategoryId;
  const zielName = targetCategoryId === AUTO_TARGET ? 'Automatisch' : formatCategoryName(categories.find((c) => String(c.id) === targetCategoryId)?.name);
  const handleZielChange = useCallback((wert) => setTargetCategoryId(wert), []);

  const handleAdd = useCallback(async (entry) => {
    if (!canAdd || addingId) return;
    setAddingId(entry.id);
    try {
      const result = await onAddCard(entry.id, {
        categoryId: targetCategoryId === AUTO_TARGET ? undefined : Number(targetCategoryId),
      });
      // Ob neu oder Dublette: die Karte liegt im Deck und gehört nicht mehr in die
      // Rangliste. Die Kachel geht sofort, statt bis zum Refetch stehen zu bleiben.
      if (result) vorschlagEntnehmen({ id: entry.id, oracleId: entry.oracleId });
      notifyAddResult(pushToast, result, { name: entry.name, onUndo: result?.undo });
      return result;
    } catch (err) {
      pushToast({ tone: 'error', message: errorMessage(err, 'Karte konnte nicht hinzugefügt werden.') });
      return null;
    } finally {
      setAddingId(null);
    }
  }, [canAdd, addingId, onAddCard, targetCategoryId, pushToast, vorschlagEntnehmen]);

  const openCardDetail = useCallback((entry) => {
    if (!entry?.oracleId) return;
    setDetail({
      scryfallId: entry.id,
      oracleId: entry.oracleId,
      fallback: {
        oracleId: entry.oracleId,
        name: entry.name,
        typeLine: entry.typeLine,
        imageUrl: entry.imageUrl,
        imageUrlBack: entry.imageUrlBack || undefined,
        imageOrientation: entry.imageOrientation,
      },
    });
  }, []);

  return (
    <div className="card-catalog">
      <Tabs value={sichtbarerTab} onValueChange={onTabChange} dense>
        <div className="card-catalog__tabrow">
          <Tabs.List>
            <Tabs.Trigger value="suggestions">Vorschläge</Tabs.Trigger>
            <Tabs.Trigger value="search">Suche</Tabs.Trigger>
            {isAdmin && (
              <Tabs.Trigger value="admin" icon={Sparkles}>
                <span className="visually-hidden">Geschickte Karten</span>
              </Tabs.Trigger>
            )}
          </Tabs.List>
          {openInWindowHref && (
            <button
              type="button"
              className="card-catalog__iconbtn"
              title="Katalog in neuem Fenster öffnen"
              aria-label="Katalog in neuem Fenster öffnen"
              onClick={() => window.open(openInWindowHref, '_blank')}
            >
              <ExternalLink size={14} aria-hidden />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="card-catalog__iconbtn"
              title="Katalog schließen"
              aria-label="Katalog schließen"
              onClick={onClose}
            >
              <X size={14} aria-hidden />
            </button>
          )}
        </div>

        {canAdd && (
          <div className="card-catalog__controls">
            <span className="card-catalog__target-label" id={`${zielId}-label`}>Hinzufügen zu</span>
            {/* Der Name des Knopfes setzt sich aus beiden Spannen zusammen: „Hinzufügen zu
                <gewähltes Ziel>". Die Beschriftung allein verschwiege, was eingestellt ist;
                den eigenen Inhalt des Knopfes kann man nicht dazunehmen, ohne ihm eine `id` zu
                geben — die überschriebe die von Radix und ließe die `aria-labelledby` des
                aufgeklappten Menüs ins Leere zeigen. */}
            <span className="visually-hidden" id={`${zielId}-wert`}>{zielName}</span>
            <Select
              size="sm"
              className="card-catalog__target"
              aria-labelledby={`${zielId}-label ${zielId}-wert`}
              value={zielWert}
              onChange={handleZielChange}
              options={targetOptions}
            />
          </div>
        )}

        <Tabs.Panel value="suggestions">
          <CatalogSuggestionsTab
            deckId={deckId}
            filters={suggestionFilters}
            onFiltersChange={patchSuggestionFilters}
            canAdd={canAdd}
            addingId={addingId}
            onAdd={handleAdd}
            onOpenCard={openCardDetail}
            dndEnabled={dndEnabled}
          />
        </Tabs.Panel>
        <Tabs.Panel value="search">
          <CatalogSearchTab
            filters={searchFilters}
            onFiltersChange={patchSearchFilters}
            canAdd={canAdd}
            addingId={addingId}
            onAdd={handleAdd}
            onOpenCard={openCardDetail}
            dndEnabled={dndEnabled}
          />
        </Tabs.Panel>
        {isAdmin && (
          <Tabs.Panel value="admin">
            <CatalogAdminTab deckId={deckUiId(deckId)} canAdd={canAdd} onAdd={handleAdd} onOpenCard={openCardDetail} />
          </Tabs.Panel>
        )}
      </Tabs>

      <CardDetail
        open={!!detail}
        onOpenChange={(next) => { if (!next) setDetail(null); }}
        oracleId={detail?.oracleId}
        scryfallId={detail?.scryfallId}
        fallback={detail?.fallback ?? null}
        section="card"
        // Der Katalog kennt keine Deck-Karte: ohne Kontext bleibt der Aktions-Slot des
        // Modals leer, das Hinzufügen läuft über das Plus auf der Kachel.
        canAct={false}
      />
    </div>
  );
}
